import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import { needsAiDataConsent } from '../../lib/aiDataApi.js';
import { AppLoader } from '../../components/loading/AppLoader.js';
import { useEnglishLevel } from '../leveling/useEnglishLevel.js';
import type { AiConsentOnlyLocationState } from '../leveling/OnboardingLevel.js';

/**
 * App-wide routing gate of the AI data consent: with a signed-in account that
 * has not accepted the current consent text, no AI-backed route renders (so a
 * deep link to /conversation cannot skip it). It renders no consent UI of its
 * own: it routes to the one place the consent is given.
 *
 * That place (redesign 2026-10-23) is the last registration step everyone
 * passes, the triage of /onboarding/level (`OnboardingLevel` →
 * `TriageScreen`): an unchecked checkbox above «Seguir», recorded before the
 * triage is saved. No step up to there sends anything to AI (sign-in, /welcome,
 * /onboarding/goal and the triage itself are plain writes), so those routes
 * are exempt. Anywhere else, an account without the consent is sent:
 *
 * - **not onboarded** → back into the registration, the way `HomeOrA0` does it
 *   (/welcome, or /onboarding/level with a placement quiz pending);
 * - **onboarded** (the store-review accounts, a future consent version bump)
 *   or `/state` unreadable (fail closed) → /onboarding/level in consent-only
 *   mode (`AiConsentStep`: only the checkbox, then back to /home).
 *
 * Exempt: no session (the route guards send it to /login), `/login` (it
 * navigates on its own right after the sign-in — gating it would race that)
 * and the registration steps above. Onboarded accounts that open /welcome or
 * /onboarding/goal are bounced to /home by those screens and land here. The
 * backend enforces the same consent (fail closed) at the voice session start
 * and on the AI REST endpoints.
 */
interface AiConsentGateProps {
  children: ReactNode;
}

const EXEMPT_PATHS = new Set(['/login', '/welcome', '/onboarding/goal', '/onboarding/level']);

const CONSENT_ONLY: AiConsentOnlyLocationState = { aiConsentOnly: true };

export function AiConsentGate({ children }: AiConsentGateProps) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  if (!user || EXEMPT_PATHS.has(pathname) || !needsAiDataConsent(user)) return <>{children}</>;
  return <ConsentRedirect />;
}

/** Rendered only for an account without the consent: reads `/state` (not AI). */
function ConsentRedirect() {
  const { state, error } = useEnglishLevel();
  if (!state && !error) {
    return (
      <div className="app auth-shell">
        <AppLoader page />
      </div>
    );
  }
  if (state && !state.onboarded) {
    return <Navigate to={state.pendingQuiz ? '/onboarding/level' : '/welcome'} replace />;
  }
  return <Navigate to="/onboarding/level" state={CONSENT_ONLY} replace />;
}
